import React from 'react';
import { useAuth } from '../context/AuthContext';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { Shield, LogIn } from 'lucide-react';
import Card from './UI/Card';
import Button from './UI/Button';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Loading animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  flex-direction: column;
  gap: 1rem;
`;

const LoadingSpinner = styled.div<{ theme: any }>`
  width: 40px;
  height: 40px;
  border: 3px solid ${props => props.theme.colors.border};
  border-top: 3px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  margin: 0;
`;

const UnauthorizedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
`;

const UnauthorizedContent = styled.div`
  text-align: center;
  max-width: 500px;
`;

const UnauthorizedIcon = styled.div<{ theme: any }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}15;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem auto;
`;

const UnauthorizedTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
`;

const UnauthorizedMessage = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 2rem 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login, consultant } = useAuth();
  const { theme } = useTheme();

  // Show loading spinner while authentication status is being determined
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner theme={theme} />
        <LoadingText theme={theme}>Checking authentication...</LoadingText>
      </LoadingContainer>
    );
  }

  // If user is not authenticated, show unauthorized message instead of redirecting
  // This provides better UX than immediate redirect
  if (!isAuthenticated) {
    return (
      <UnauthorizedContainer>
        <Card>
          <UnauthorizedContent>
            <UnauthorizedIcon theme={theme}>
              <Shield size={40} />
            </UnauthorizedIcon>
            <UnauthorizedTitle theme={theme}>
              Authentication Required
            </UnauthorizedTitle>
            <UnauthorizedMessage theme={theme}>
              You need to be logged in to access this page. Please sign in with your tax consultant credentials to continue.
            </UnauthorizedMessage>
            <ActionButtons>
              <Button onClick={() => login()}>
                <LogIn size={16} style={{ marginRight: '0.5rem' }} />
                Sign In
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </ActionButtons>
          </UnauthorizedContent>
        </Card>
      </UnauthorizedContainer>
    );
  }

  // If authenticated, render the protected content
  if (isAuthenticated && !consultant) {
    return (
      <UnauthorizedContainer>
        <Card>
          <UnauthorizedContent>
            <UnauthorizedIcon theme={theme}>
              <Shield size={40} />
            </UnauthorizedIcon>
            <UnauthorizedTitle theme={theme}>
              Unable to Load Profile
            </UnauthorizedTitle>
            <UnauthorizedMessage theme={theme}>
              We couldn't load your consultant profile. Please sign in again.
            </UnauthorizedMessage>
            <ActionButtons>
              <Button onClick={() => login()}>
                <LogIn size={16} style={{ marginRight: '0.5rem' }} />
                Sign In
              </Button>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </ActionButtons>
          </UnauthorizedContent>
        </Card>
      </UnauthorizedContainer>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
